import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus, Trash2, Pencil, FileDown, Mail, Send, CheckCircle2, XCircle,
  ShoppingCart, MessageSquareQuote, ClipboardList, MessageCircle, Eye,
  Trophy, Copy, Link as LinkIcon, Users, FileSpreadsheet, FileCog, MailPlus,
  ShieldCheck, ShieldOff, Activity as ActivityIcon,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { fmtDate, fmtPrice } from "@/lib/format";
import { nextRfqNudgeDate, relativeDayHint } from "@/lib/reminders";
import { ActivityLog } from "@/components/ActivityLog";
import { RfqDecisionTimeline } from "@/components/RfqDecisionTimeline";
import { ChatPanel } from "@/components/ChatPanel";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { EmailDocumentDialog } from "@/components/EmailDocumentDialog";
import { buildWaUrl } from "@/lib/whatsapp";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const RFQ_STATUSES = ["draft", "sent", "responded", "closed", "cancelled"];
const CURRENCIES = ["USD", "IDR", "EUR", "AUD", "SGD"];

function StatusPill({ value }) {
  const map = {
    draft: "bg-slate-100 text-slate-700 ring-slate-200",
    sent: "bg-amber-50 text-amber-800 ring-amber-200",
    responded: "bg-sky-50 text-sky-700 ring-sky-200",
    closed: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    cancelled: "bg-red-50 text-red-700 ring-red-200",
  };
  return (
    <span
      data-testid={`rfq-status-pill-${value}`}
      className={`inline-block px-2 py-0.5 rounded-md text-[11px] font-medium ring-1 ring-inset ${map[value] || map.draft}`}
    >
      {value}
    </span>
  );
}

const emptyLine = {
  item_id: "",
  name: "",
  specification: "",
  brand: "",
  model: "",
  qty: 1,
  unit: "unit",
  target_price: 0,
};

const emptyForm = {
  supplier_id: "",
  reference: "",
  issue_date: new Date().toISOString().slice(0, 10),
  valid_until: "",
  currency: "USD",
  notes: "",
  lines: [{ ...emptyLine }],
};

export default function RequestForQuotations() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [detailRfq, setDetailRfq] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [addQuoteOpen, setAddQuoteOpen] = useState(false);
  const [inviteSupplierId, setInviteSupplierId] = useState("");
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [approverName, setApproverName] = useState("");
  const [approverNotes, setApproverNotes] = useState("");

  const { data: rfqs = [], isLoading } = useQuery({
    queryKey: ["rfqs"],
    queryFn: () => api.listRfqs(),
  });
  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers"],
    queryFn: () => api.listSuppliers(),
  });
  const { data: items = [] } = useQuery({
    queryKey: ["items"],
    queryFn: () => api.listItems(),
  });
  const { data: reminderSettings } = useQuery({
    queryKey: ["reminder-settings"],
    queryFn: () => api.getReminderSettings(),
    staleTime: 5 * 60_000,
  });
  const rfqNudgeDays = reminderSettings?.rfq_nudge_days || 3;

  // Deep-link support: ?open=<rfqId> — open the target RFQ detail once the
  // list has loaded (used by the Dashboard "Open RFQs" widget).
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightId, setHighlightId] = useState(null);
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || !rfqs.length) return;
    const target = rfqs.find((r) => r.id === openId);
    if (target) {
      setDetailRfq(target);
      setHighlightId(openId);
      // Fade the highlight after a few seconds so the pulse feels finite.
      const timer = setTimeout(() => setHighlightId(null), 3500);
      // Clear the param so refresh / back doesn't reopen it forever.
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
      return () => clearTimeout(timer);
    }
  }, [rfqs]);

  const counts = useMemo(() => {
    const c = { all: rfqs.length, draft: 0, sent: 0, responded: 0, closed: 0, cancelled: 0 };
    for (const r of rfqs) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [rfqs]);

  const filtered = rfqs.filter((r) => filter === "all" || r.status === filter);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, lines: [{ ...emptyLine }] });
    setDialogOpen(true);
  };
  const openEdit = (rfq) => {
    setEditing(rfq);
    setForm({
      supplier_id: rfq.supplier_id || "",
      reference: rfq.reference || "",
      issue_date: rfq.issue_date || new Date().toISOString().slice(0, 10),
      valid_until: rfq.valid_until || "",
      currency: rfq.currency || "USD",
      notes: rfq.notes || "",
      lines: (rfq.lines && rfq.lines.length > 0)
        ? rfq.lines.map((l) => ({
            item_id: l.item_id || "",
            name: l.name || "",
            specification: l.specification || "",
            brand: l.brand || "",
            model: l.model || "",
            qty: l.qty || 1,
            unit: l.unit || "unit",
            target_price: l.target_price || 0,
          }))
        : [{ ...emptyLine }],
    });
    setDialogOpen(true);
  };

  const setLine = (idx, patch) => {
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    }));
  };
  const removeLine = (idx) => {
    setForm((f) => ({
      ...f,
      lines: f.lines.length > 1 ? f.lines.filter((_, i) => i !== idx) : f.lines,
    }));
  };
  const addLine = () => {
    setForm((f) => ({ ...f, lines: [...f.lines, { ...emptyLine }] }));
  };
  const pickInventoryForLine = (idx, itemId) => {
    if (!itemId) {
      setLine(idx, { item_id: "" });
      return;
    }
    const it = items.find((x) => x.id === itemId);
    if (!it) {
      setLine(idx, { item_id: itemId });
      return;
    }
    setLine(idx, {
      item_id: it.id,
      name: it.name || "",
      specification: it.specification || "",
      brand: it.brand || "",
      model: it.model || "",
      unit: it.unit || "unit",
      target_price: Number(it.price || 0),
    });
  };

  const saveMut = useMutation({
    mutationFn: (payload) =>
      editing ? api.updateRfq(editing.id, payload) : api.createRfq(payload),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ["rfqs"] });
      setDialogOpen(false);
      setEditing(null);
      toast.success(editing ? "RFQ updated" : `Created ${doc.rfq_number}`);
      if (!editing) setDetailRfq(doc);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }) => api.updateRfqStatus(id, status),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ["rfqs"] });
      if (detailRfq && detailRfq.id === doc.id) setDetailRfq(doc);
      toast.success(`Marked as ${doc.status}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.deleteRfq(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rfqs"] });
      setConfirmDeleteId(null);
      if (detailRfq && detailRfq.id === confirmDeleteId) setDetailRfq(null);
      toast.success("RFQ removed");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const toPoMut = useMutation({
    mutationFn: (id) => api.createPoFromRfq(id),
    onSuccess: (po) => {
      qc.invalidateQueries({ queryKey: ["rfqs"] });
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      if (detailRfq) setDetailRfq({ ...detailRfq, status: "closed" });
      if (po.zero_amount_lines > 0) {
        toast.warning(
          `Created PO ${po.po_number}, but ${po.zero_amount_lines} line(s) have no amount — open Purchase Orders and edit the prices before sending`,
          { duration: 10000 },
        );
      } else {
        toast.success(`Created PO ${po.po_number}`);
      }
    },
    onError: (e) => {
      if (e?.response?.status === 409 && e?.response?.data?.needs_approval) {
        setApprovalOpen(true);
        toast.warning("Please record a manager approval before creating the PO");
      } else {
        toast.error(e?.response?.data?.detail || "Conversion failed");
      }
    },
  });

  const addSupplierMut = useMutation({
    mutationFn: (supplier_id) => api.addRfqSupplier(detailRfq.id, supplier_id),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["rfqs"] });
      setDetailRfq(updated);
      setInviteSupplierId("");
      toast.success("Supplier invited");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to invite supplier"),
  });
  const removeSupplierMut = useMutation({
    mutationFn: (supplier_id) => api.removeRfqSupplier(detailRfq.id, supplier_id),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["rfqs"] });
      setDetailRfq(updated);
    },
  });
  const removeQuoteMut = useMutation({
    mutationFn: (quote_id) => api.removeRfqQuote(detailRfq.id, quote_id),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["rfqs"] });
      setDetailRfq(updated);
      toast.success("Quote removed");
    },
  });
  const pickWinnerMut = useMutation({
    mutationFn: (quote_id) => api.selectRfqWinner(detailRfq.id, quote_id),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["rfqs"] });
      setDetailRfq(updated);
      toast.success("Winner selected");
    },
  });
  const ensureLinkMut = useMutation({
    mutationFn: () => api.ensureRfqPublicLink(detailRfq.id),
    onSuccess: (res) => {
      const url = `${window.location.origin}/rfq/${res.public_token}`;
      try {
        navigator.clipboard.writeText(url);
        toast.success("Portal link copied to clipboard");
      } catch {
        toast.info(url);
      }
      setDetailRfq({ ...detailRfq, public_token: res.public_token });
    },
  });
  const addQuoteMut = useMutation({
    mutationFn: (payload) => api.addRfqQuote(detailRfq.id, payload),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["rfqs"] });
      setDetailRfq(updated);
      setAddQuoteOpen(false);
      toast.success("Quote recorded");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to record quote"),
  });

  const broadcastMut = useMutation({
    mutationFn: () => api.broadcastRfqPortal(detailRfq.id, window.location.origin),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["rfqs"] });
      setDetailRfq({ ...detailRfq, public_token: res.public_token });
      if (res.sent.length > 0) {
        toast.success(`Emailed portal link to ${res.sent.length} supplier${res.sent.length > 1 ? "s" : ""}`);
      }
      if (res.skipped.length > 0) {
        toast.warning(`Skipped ${res.skipped.length} supplier${res.skipped.length > 1 ? "s" : ""} (missing email or send failed)`);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Broadcast failed"),
  });

  const approveMut = useMutation({
    mutationFn: () =>
      api.approveRfqWinner(detailRfq.id, {
        approver_name: approverName,
        notes: approverNotes,
      }),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["rfqs"] });
      setDetailRfq(updated);
      setApprovalOpen(false);
      setApproverName("");
      setApproverNotes("");
      toast.success("Winner approved");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Approval failed"),
  });
  const revokeApprovalMut = useMutation({
    mutationFn: () => api.revokeRfqWinnerApproval(detailRfq.id),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["rfqs"] });
      setDetailRfq(updated);
      toast.success("Approval revoked");
    },
  });

  const downloadBlob = async (blobPromise, filename) => {
    try {
      const res = await blobPromise;
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  const submit = () => {
    if (!form.supplier_id) {
      toast.error("Pick a supplier");
      return;
    }
    const validLines = form.lines.filter((l) => (l.name || "").trim());
    if (validLines.length === 0) {
      toast.error("Add at least one line with an item name");
      return;
    }
    saveMut.mutate({ ...form, lines: validLines });
  };

  const openWhatsapp = (rfq) => {
    const supplier = rfq.supplier_snapshot || {};
    const to = supplier.phone || "";
    const contact = (supplier.contact_person || supplier.name || "team").split(" ")[0];
    const validUntil = rfq.valid_until ? ` by ${fmtDate(rfq.valid_until)}` : "";
    const text = `Hi ${contact}, we've sent through KINOS RFQ *${rfq.rfq_number}* for your quotation. `
      + `Please review the attached items and share your best pricing${validUntil}. Thanks — KINOS.`;
    window.open(buildWaUrl(to, text), "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="rfqs-page">
      <PageHeader
        overline="procurement"
        title="Requests for Quotation"
        subtitle="Ask suppliers for pricing before issuing a Purchase Order."
        actions={
          <Button
            data-testid="rfq-new-btn"
            onClick={openCreate}
            className="bg-primary hover:bg-primary/90"
          >
            <Plus className="size-4 mr-1.5" /> New RFQ
          </Button>
        }
      />

      {/* KPI ribbon */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <KPI testid="rfq-kpi-total" label="Total RFQs" icon={MessageSquareQuote} value={counts.all} tone="slate" />
        <KPI testid="rfq-kpi-draft" label="Draft" icon={Pencil} value={counts.draft || 0} tone="slate" />
        <KPI testid="rfq-kpi-sent" label="Sent" icon={Send} value={counts.sent || 0} tone="amber" />
        <KPI testid="rfq-kpi-responded" label="Responded" icon={CheckCircle2} value={counts.responded || 0} tone="sky" />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="rfq-tabs">
        {[
          { id: "all", label: "All", count: counts.all },
          { id: "draft", label: "Draft", count: counts.draft || 0 },
          { id: "sent", label: "Sent", count: counts.sent || 0 },
          { id: "responded", label: "Responded", count: counts.responded || 0 },
          { id: "closed", label: "Closed", count: counts.closed || 0 },
          { id: "cancelled", label: "Cancelled", count: counts.cancelled || 0 },
        ].map((tab) => {
          const active = filter === tab.id;
          return (
            <button
              key={tab.id}
              data-testid={`rfq-tab-${tab.id}`}
              onClick={() => setFilter(tab.id)}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium ring-1 ring-inset transition ${
                active
                  ? "bg-slate-800 text-white ring-slate-800"
                  : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"
              }`}
            >
              {tab.label}
              <span className={`text-[10px] mono px-1.5 py-0.5 rounded-full ${
                active ? "bg-white/25" : "bg-slate-100 text-slate-600"
              }`}>{tab.count}</span>
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No requests for quotation yet"
          hint="Create an RFQ to ask a supplier for pricing"
          action={
            <Button
              data-testid="rfq-empty-btn"
              onClick={openCreate}
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="size-4 mr-1.5" /> New RFQ
            </Button>
          }
        />
      ) : (
        <div className="border border-border rounded-md bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline">RFQ #</TableHead>
                <TableHead className="overline">Supplier</TableHead>
                <TableHead className="overline">Reference</TableHead>
                <TableHead className="overline">Issue</TableHead>
                <TableHead className="overline">Valid until</TableHead>
                <TableHead className="overline text-right">Lines</TableHead>
                <TableHead className="overline">Status</TableHead>
                <TableHead className="overline text-right w-40">&nbsp;</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow
                  key={r.id}
                  data-testid={`rfq-row-${r.id}`}
                  data-highlighted={highlightId === r.id ? "true" : undefined}
                  className={`cursor-pointer transition-colors ${
                    highlightId === r.id
                      ? "bg-amber-50 ring-2 ring-inset ring-amber-300 animate-pulse"
                      : ""
                  }`}
                  onClick={() => setDetailRfq(r)}
                >
                  <TableCell className="mono font-medium text-primary">
                    {r.rfq_number}
                  </TableCell>
                  <TableCell className="font-medium">
                    {r.supplier_snapshot?.name || "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.reference || "—"}
                  </TableCell>
                  <TableCell className="mono text-xs">{fmtDate(r.issue_date)}</TableCell>
                  <TableCell className="mono text-xs">
                    <div className="flex items-center gap-1.5">
                      <span>{r.valid_until ? fmtDate(r.valid_until) : "—"}</span>
                      {(() => {
                        if (r.status !== "sent") return null;
                        if ((r.quotes || []).length > 0) return null;
                        const next = nextRfqNudgeDate({
                          validUntil: r.valid_until,
                          remindedAt: r.reminder_sent_at,
                          nudgeDays: rfqNudgeDays,
                        });
                        if (!next) return null;
                        return (
                          <span
                            data-testid={`rfq-next-nudge-${r.id}`}
                            title={`Auto-nudge ${relativeDayHint(next)} (${next})`}
                            className="inline-block px-1.5 py-0.5 rounded-md text-[10px] font-medium ring-1 ring-inset bg-sky-50 text-sky-700 ring-sky-200"
                          >
                            nudge {relativeDayHint(next)}
                          </span>
                        );
                      })()}
                    </div>
                  </TableCell>
                  <TableCell className="text-right mono">{(r.lines || []).length}</TableCell>
                  <TableCell>
                    <StatusPill value={r.status} />
                  </TableCell>
                  <TableCell
                    className="text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="inline-flex gap-1">
                      <Button
                        data-testid={`rfq-preview-${r.id}`}
                        variant="ghost" size="sm"
                        className="h-7 w-7 p-0 text-primary"
                        title="Preview PDF"
                        onClick={() => { setDetailRfq(r); setPreviewOpen(true); }}
                      >
                        <Eye className="size-3.5" />
                      </Button>
                      <Button
                        data-testid={`rfq-pdf-${r.id}`}
                        variant="ghost" size="sm"
                        className="h-7 w-7 p-0 text-primary"
                        title="Download PDF"
                        onClick={() => api.openRfqPdf(r.id)}
                      >
                        <FileDown className="size-3.5" />
                      </Button>
                      <Button
                        data-testid={`rfq-email-${r.id}`}
                        variant="ghost" size="sm"
                        className="h-7 w-7 p-0 text-primary"
                        title="Email supplier"
                        onClick={() => { setDetailRfq(r); setEmailOpen(true); }}
                      >
                        <Mail className="size-3.5" />
                      </Button>
                      <Button
                        data-testid={`rfq-wa-${r.id}`}
                        variant="ghost" size="sm"
                        className="h-7 w-7 p-0 text-emerald-600"
                        title="Share via WhatsApp"
                        onClick={() => openWhatsapp(r)}
                      >
                        <MessageCircle className="size-3.5" />
                      </Button>
                      <Button
                        data-testid={`rfq-edit-${r.id}`}
                        variant="ghost" size="sm"
                        className="h-7 w-7 p-0"
                        title="Edit"
                        onClick={() => openEdit(r)}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button
                        data-testid={`rfq-delete-${r.id}`}
                        variant="ghost" size="sm"
                        className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                        title="Delete"
                        onClick={() => setConfirmDeleteId(r.id)}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!v) setEditing(null); setDialogOpen(v); }}>
        <DialogContent data-testid="rfq-dialog" className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? `Edit ${editing.rfq_number}` : "New Request for Quotation"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Pick a supplier and list the items you want a quotation for. Target
              prices are optional — they help the supplier see your reference budget.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 sm:col-span-5">
              <Label className="overline">Supplier *</Label>
              <Select
                value={form.supplier_id || undefined}
                onValueChange={(v) => setForm({ ...form, supplier_id: v })}
              >
                <SelectTrigger data-testid="rfq-form-supplier">
                  <SelectValue placeholder="Select a supplier…" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {suppliers.length === 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      No suppliers — add one first in the Suppliers page
                    </div>
                  )}
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-6 sm:col-span-3">
              <Label className="overline">Reference / project</Label>
              <Input
                data-testid="rfq-form-reference"
                value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                placeholder="e.g. MoH tender 2026"
              />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <Label className="overline">Issue date</Label>
              <Input
                data-testid="rfq-form-issue-date"
                type="date"
                value={form.issue_date}
                onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
              />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <Label className="overline">Valid until</Label>
              <Input
                data-testid="rfq-form-valid-until"
                type="date"
                value={form.valid_until}
                onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
              />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <Label className="overline">Currency</Label>
              <Select
                value={form.currency}
                onValueChange={(v) => setForm({ ...form, currency: v })}
              >
                <SelectTrigger data-testid="rfq-form-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-4 border border-border rounded-md">
            <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-slate-50">
              <span className="overline">Line items</span>
              <Button
                data-testid="rfq-add-line"
                variant="outline" size="sm"
                onClick={addLine}
                type="button"
              >
                <Plus className="size-3.5 mr-1" /> Add line
              </Button>
            </div>
            <div className="p-3 space-y-3">
              {form.lines.map((ln, idx) => (
                <div
                  key={idx}
                  data-testid={`rfq-line-${idx}`}
                  className="grid grid-cols-12 gap-2 items-start border border-border rounded-md p-2 bg-white"
                >
                  <div className="col-span-12 sm:col-span-3">
                    <Label className="overline text-[10px]">Inventory (optional)</Label>
                    <Select
                      value={ln.item_id || undefined}
                      onValueChange={(v) => pickInventoryForLine(idx, v)}
                    >
                      <SelectTrigger data-testid={`rfq-line-${idx}-inv`} className="h-9">
                        <SelectValue placeholder="Pick from catalog…" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {items.map((it) => (
                          <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-12 sm:col-span-3">
                    <Label className="overline text-[10px]">Item name *</Label>
                    <Input
                      data-testid={`rfq-line-${idx}-name`}
                      value={ln.name}
                      onChange={(e) => setLine(idx, { name: e.target.value })}
                      placeholder="e.g. HP ProBook 450 G10"
                    />
                  </div>
                  <div className="col-span-12 sm:col-span-6">
                    <Label className="overline text-[10px]">Specification</Label>
                    <Textarea
                      data-testid={`rfq-line-${idx}-spec`}
                      rows={2}
                      value={ln.specification}
                      onChange={(e) => setLine(idx, { specification: e.target.value })}
                      placeholder="RAM 16GB - SSD 512GB - Windows 11 Pro …"
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Label className="overline text-[10px]">Brand</Label>
                    <Input
                      data-testid={`rfq-line-${idx}-brand`}
                      value={ln.brand}
                      onChange={(e) => setLine(idx, { brand: e.target.value })}
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Label className="overline text-[10px]">Model</Label>
                    <Input
                      data-testid={`rfq-line-${idx}-model`}
                      value={ln.model}
                      onChange={(e) => setLine(idx, { model: e.target.value })}
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-1">
                    <Label className="overline text-[10px]">Qty</Label>
                    <Input
                      data-testid={`rfq-line-${idx}-qty`}
                      type="number" min="0" step="1"
                      value={ln.qty}
                      onChange={(e) => setLine(idx, { qty: Number(e.target.value) })}
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Label className="overline text-[10px]">Unit</Label>
                    <Input
                      data-testid={`rfq-line-${idx}-unit`}
                      value={ln.unit}
                      onChange={(e) => setLine(idx, { unit: e.target.value })}
                      placeholder="unit / box / set"
                    />
                  </div>
                  <div className="col-span-8 sm:col-span-4">
                    <Label className="overline text-[10px]">
                      Target price ({form.currency}) — optional
                    </Label>
                    <Input
                      data-testid={`rfq-line-${idx}-target`}
                      type="number" min="0" step="0.01"
                      value={ln.target_price}
                      onChange={(e) => setLine(idx, { target_price: Number(e.target.value) })}
                    />
                  </div>
                  <div className="col-span-12 sm:col-span-1 flex sm:justify-end">
                    <Button
                      data-testid={`rfq-line-${idx}-remove`}
                      variant="ghost" size="sm"
                      className="text-red-600 hover:text-red-700 h-9 w-9 p-0 mt-4"
                      onClick={() => removeLine(idx)}
                      type="button"
                      title="Remove line"
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3">
            <Label className="overline">Notes for the supplier</Label>
            <Textarea
              data-testid="rfq-form-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. Delivery to KINOS Dili warehouse, warranty ≥ 12 months."
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="rfq-form-cancel"
            >
              Cancel
            </Button>
            <Button
              data-testid="rfq-form-save"
              disabled={saveMut.isPending}
              className="bg-primary hover:bg-primary/90"
              onClick={submit}
            >
              {saveMut.isPending ? "Saving…" : editing ? "Save changes" : "Create RFQ"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detailRfq && !previewOpen && !emailOpen} onOpenChange={(v) => !v && setDetailRfq(null)}>
        <DialogContent data-testid="rfq-detail-dialog" className="max-w-4xl max-h-[92vh] overflow-y-auto">
          {detailRfq && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <span className="font-mono text-primary">{detailRfq.rfq_number}</span>
                  <StatusPill value={detailRfq.status} />
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {detailRfq.supplier_snapshot?.name} · Issued {fmtDate(detailRfq.issue_date)}
                  {detailRfq.valid_until && ` · Valid until ${fmtDate(detailRfq.valid_until)}`}
                  {detailRfq.reference && ` · Ref: ${detailRfq.reference}`}
                </DialogDescription>
              </DialogHeader>

              <div className="border border-border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-b border-border">
                      <TableHead className="overline">Item</TableHead>
                      <TableHead className="overline text-right">Qty</TableHead>
                      <TableHead className="overline">Unit</TableHead>
                      <TableHead className="overline text-right">Target price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(detailRfq.lines || []).map((ln, i) => (
                      <TableRow key={i} data-testid={`rfq-detail-line-${i}`}>
                        <TableCell className="font-medium">
                          {ln.name}
                          {ln.specification && (
                            <div className="text-[10px] text-muted-foreground line-clamp-3 whitespace-pre-wrap">
                              {ln.specification}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right mono">{ln.qty}</TableCell>
                        <TableCell className="text-xs">{ln.unit || "unit"}</TableCell>
                        <TableCell className="text-right mono">
                          {ln.target_price > 0
                            ? fmtPrice(ln.target_price, detailRfq.currency)
                            : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {detailRfq.notes && (
                <div className="rounded-md bg-slate-50 border border-border p-3">
                  <p className="overline mb-1">Notes</p>
                  <p className="text-xs whitespace-pre-wrap">{detailRfq.notes}</p>
                </div>
              )}

              {detailRfq.converted_po_number && (
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800 inline-flex items-center gap-2">
                  <ShoppingCart className="size-3.5" />
                  Converted to purchase order <b className="mono">{detailRfq.converted_po_number}</b>
                </div>
              )}

              {/* Portal analytics */}
              {((detailRfq.portal_events || []).length > 0
                || detailRfq.public_token) && (
                <div
                  className="border border-border rounded-md p-3 space-y-1"
                  data-testid="rfq-portal-analytics"
                >
                  <p className="overline flex items-center gap-1.5">
                    <ActivityIcon className="size-3.5" /> Portal analytics
                  </p>
                  {(() => {
                    const events = detailRfq.portal_events || [];
                    const views = events.filter((e) => e.event === "viewed");
                    const subs = events.filter((e) => e.event === "submitted");
                    const lastView = views[views.length - 1];
                    return (
                      <div className="text-xs text-muted-foreground space-y-0.5">
                        <p>
                          <b className="text-foreground mono">{views.length}</b> view{views.length === 1 ? "" : "s"}
                          {lastView && ` · last ${relativeDayHint(lastView.at)} from IP ${lastView.ip || "?"}`}
                        </p>
                        <p>
                          <b className="text-foreground mono">{subs.length}</b> portal submission{subs.length === 1 ? "" : "s"}
                          {subs.length > 0 &&
                            ` · last from ${subs[subs.length - 1].supplier_name || "?"}`}
                        </p>
                        {views.length === 0 && detailRfq.public_token && (
                          <p className="text-[10px] italic">
                            The portal link exists but no supplier has opened it yet.
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Portal link */}
              <div className="border border-border rounded-md p-3 space-y-2" data-testid="rfq-portal-section">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="overline">Supplier portal link</p>
                    <p className="text-[11px] text-muted-foreground">
                      Share this link so a supplier can submit prices directly online.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid="rfq-portal-ensure"
                      variant="outline" size="sm"
                      onClick={() => ensureLinkMut.mutate()}
                      disabled={ensureLinkMut.isPending}
                    >
                      <LinkIcon className="size-3.5 mr-1.5" />
                      {detailRfq.public_token ? "Copy link" : "Generate link"}
                    </Button>
                    <Button
                      data-testid="rfq-portal-broadcast"
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      disabled={broadcastMut.isPending}
                      onClick={() => broadcastMut.mutate()}
                      title="Email portal link + PDF to every invited supplier"
                    >
                      <MailPlus className="size-3.5 mr-1.5" />
                      {broadcastMut.isPending ? "Sending…" : "Email link to invited suppliers"}
                    </Button>
                  </div>
                </div>
                {detailRfq.public_token && (
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid="rfq-portal-link"
                      readOnly
                      value={`${window.location.origin}/rfq/${detailRfq.public_token}`}
                      className="h-8 text-xs font-mono"
                    />
                    <Button
                      size="sm" variant="ghost"
                      data-testid="rfq-portal-copy"
                      onClick={() => {
                        try {
                          navigator.clipboard.writeText(`${window.location.origin}/rfq/${detailRfq.public_token}`);
                          toast.success("Link copied");
                        } catch { /* ignore */ }
                      }}
                    >
                      <Copy className="size-3.5" />
                    </Button>
                  </div>
                )}
                {detailRfq.last_broadcast_at && (
                  <p className="text-[10px] text-muted-foreground">
                    Last broadcast: {new Date(detailRfq.last_broadcast_at).toLocaleString()}
                  </p>
                )}
              </div>

              {/* Invited suppliers */}
              <div className="border border-border rounded-md p-3 space-y-2" data-testid="rfq-suppliers-section">
                <p className="overline flex items-center gap-1.5">
                  <Users className="size-3.5" /> Invited suppliers
                </p>
                <div className="flex flex-wrap gap-1.5">
                  <span
                    data-testid="rfq-primary-supplier-chip"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary/10 text-primary text-[11px] font-medium ring-1 ring-inset ring-primary/20"
                  >
                    <Trophy className="size-3" />
                    {detailRfq.supplier_snapshot?.name} <span className="opacity-60">(primary)</span>
                  </span>
                  {(detailRfq.additional_supplier_snapshots || []).map((s) => (
                    <span
                      key={s.id}
                      data-testid={`rfq-extra-supplier-${s.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 text-slate-700 text-[11px] font-medium ring-1 ring-inset ring-slate-200"
                    >
                      {s.name}
                      <button
                        title="Remove supplier"
                        onClick={() => removeSupplierMut.mutate(s.id)}
                        className="ml-1 text-slate-500 hover:text-red-600"
                        data-testid={`rfq-remove-supplier-${s.id}`}
                      >
                        <XCircle className="size-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={inviteSupplierId || undefined}
                    onValueChange={setInviteSupplierId}
                  >
                    <SelectTrigger data-testid="rfq-invite-supplier" className="h-8 text-xs">
                      <SelectValue placeholder="Invite another supplier…" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {suppliers
                        .filter((s) => s.id !== detailRfq.supplier_id
                          && !(detailRfq.additional_supplier_ids || []).includes(s.id))
                        .map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Button
                    data-testid="rfq-invite-btn"
                    size="sm"
                    disabled={!inviteSupplierId || addSupplierMut.isPending}
                    onClick={() => inviteSupplierId && addSupplierMut.mutate(inviteSupplierId)}
                  >
                    Invite
                  </Button>
                </div>
              </div>

              {/* Supplier quotes comparison */}
              <div className="border border-border rounded-md p-3 space-y-2" data-testid="rfq-quotes-section">
                <div className="flex items-center justify-between">
                  <p className="overline">Supplier quotes {(detailRfq.quotes || []).length > 0 && `· ${(detailRfq.quotes || []).length}`}</p>
                  <div className="flex gap-2">
                    {(detailRfq.quotes || []).length > 0 && (
                      <>
                        <Button
                          data-testid="rfq-compare-pdf"
                          variant="outline" size="sm"
                          onClick={() =>
                            downloadBlob(
                              api.fetchRfqComparePdfBlob(detailRfq.id),
                              `${detailRfq.rfq_number}-compare.pdf`,
                            )
                          }
                          title="Approval-ready compare PDF"
                        >
                          <FileCog className="size-3.5 mr-1" /> Compare PDF
                        </Button>
                        <Button
                          data-testid="rfq-compare-csv"
                          variant="outline" size="sm"
                          onClick={() =>
                            downloadBlob(
                              api.downloadRfqCompareCsv(detailRfq.id),
                              `${detailRfq.rfq_number}-compare.csv`,
                            )
                          }
                        >
                          <FileSpreadsheet className="size-3.5 mr-1" /> CSV
                        </Button>
                      </>
                    )}
                    <Button
                      data-testid="rfq-add-quote-btn"
                      variant="outline" size="sm"
                      onClick={() => setAddQuoteOpen(true)}
                    >
                      <Plus className="size-3.5 mr-1" /> Record quote
                    </Button>
                  </div>
                </div>
                {(detailRfq.quotes || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No quotes yet. Share the portal link above or record a quote manually.
                  </p>
                ) : (
                  <QuotesCompareTable
                    rfq={detailRfq}
                    onSelectWinner={(id) => pickWinnerMut.mutate(id)}
                    onRemoveQuote={(id) => removeQuoteMut.mutate(id)}
                  />
                )}
              </div>

              <ActivityLog resourceType="rfq" resourceId={detailRfq.id} />

              <RfqDecisionTimeline rfqId={detailRfq.id} />

              {/* Staff-side portal chat */}
              <StaffPortalChat
                rfq={detailRfq}
                onUpdated={(updated) => {
                  qc.invalidateQueries({ queryKey: ["rfqs"] });
                  qc.invalidateQueries({
                    queryKey: ["audit-logs", "rfq", detailRfq.id, "decision-timeline"],
                  });
                  setDetailRfq(updated);
                }}
              />

              {/* Winner approval banner */}
              {detailRfq.winner_quote_id && (
                <div
                  data-testid="rfq-approval-section"
                  className={`border rounded-md p-3 ${
                    detailRfq.winner_approval
                      ? "border-emerald-200 bg-emerald-50"
                      : "border-amber-200 bg-amber-50"
                  }`}
                >
                  {detailRfq.winner_approval ? (
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-xs text-emerald-800 flex items-center gap-2">
                        <ShieldCheck className="size-4" />
                        <span data-testid="rfq-approval-status">
                          Approved by <b>{detailRfq.winner_approval.approver_name}</b>{" "}
                          on {new Date(detailRfq.winner_approval.signed_at).toLocaleString()}
                        </span>
                        {detailRfq.winner_approval.notes && (
                          <span className="italic text-emerald-700">
                            — {detailRfq.winner_approval.notes}
                          </span>
                        )}
                      </div>
                      <Button
                        data-testid="rfq-approval-revoke"
                        variant="outline" size="sm"
                        className="border-amber-300 text-amber-800 hover:bg-amber-100"
                        onClick={() => revokeApprovalMut.mutate()}
                        disabled={revokeApprovalMut.isPending}
                      >
                        <ShieldOff className="size-3.5 mr-1.5" /> Revoke
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <p className="text-xs text-amber-800">
                        A winning quote is selected but not yet approved. A manager
                        must sign off before this RFQ can be converted to a Purchase Order.
                      </p>
                      <Button
                        data-testid="rfq-approval-open"
                        size="sm"
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={() => setApprovalOpen(true)}
                      >
                        <ShieldCheck className="size-3.5 mr-1.5" /> Record approval
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <DialogFooter className="flex-wrap gap-2">
                <Button
                  variant="outline"
                  data-testid="rfq-detail-preview"
                  onClick={() => setPreviewOpen(true)}
                >
                  <Eye className="size-4 mr-1.5" /> Preview PDF
                </Button>
                <Button
                  variant="outline"
                  data-testid="rfq-detail-pdf"
                  onClick={() => api.openRfqPdf(detailRfq.id)}
                >
                  <FileDown className="size-4 mr-1.5" /> Download PDF
                </Button>
                <Button
                  variant="outline"
                  data-testid="rfq-detail-email"
                  onClick={() => setEmailOpen(true)}
                >
                  <Mail className="size-4 mr-1.5" /> Email supplier
                </Button>
                <Button
                  variant="outline"
                  data-testid="rfq-detail-wa"
                  className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  onClick={() => openWhatsapp(detailRfq)}
                >
                  <MessageCircle className="size-4 mr-1.5" /> WhatsApp
                </Button>
                <div className="w-full flex flex-wrap justify-end gap-2 mt-2 border-t border-border pt-3">
                  {RFQ_STATUSES.filter((s) => s !== detailRfq.status).map((s) => (
                    <Button
                      key={s}
                      data-testid={`rfq-status-${s}`}
                      variant={s === "closed" ? "default" : "outline"}
                      className={
                        s === "closed"
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                          : s === "cancelled"
                          ? "text-red-600 border-red-200 hover:bg-red-50"
                          : ""
                      }
                      disabled={statusMut.isPending}
                      onClick={() =>
                        statusMut.mutate({ id: detailRfq.id, status: s })
                      }
                    >
                      {s === "sent" && <Send className="size-3.5 mr-1.5" />}
                      {s === "responded" && <ClipboardList className="size-3.5 mr-1.5" />}
                      {s === "closed" && <CheckCircle2 className="size-3.5 mr-1.5" />}
                      {s === "cancelled" && <XCircle className="size-3.5 mr-1.5" />}
                      Mark {s}
                    </Button>
                  ))}
                  <Button
                    data-testid="rfq-to-po"
                    disabled={
                      toPoMut.isPending
                      || detailRfq.status === "closed"
                      || !!detailRfq.converted_po_id
                      || (!!detailRfq.winner_quote_id && !detailRfq.winner_approval)
                    }
                    className="bg-primary hover:bg-primary/90"
                    onClick={() => toPoMut.mutate(detailRfq.id)}
                    title={
                      detailRfq.winner_quote_id && !detailRfq.winner_approval
                        ? "Manager approval required first"
                        : undefined
                    }
                  >
                    <ShoppingCart className="size-3.5 mr-1.5" />
                    Convert to Purchase Order
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF preview */}
      {detailRfq && (
        <PdfPreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          title={`RFQ ${detailRfq.rfq_number}`}
          fetchBlob={() => api.fetchRfqPdfBlob(detailRfq.id)}
          filename={`${detailRfq.rfq_number}.pdf`}
          fetchPngBlob={() => api.fetchRfqPngBlob(detailRfq.id)}
          pngFilename={`${detailRfq.rfq_number}.png`}
        />
      )}

      {/* Email supplier */}
      {detailRfq && (
        <EmailDocumentDialog
          open={emailOpen}
          onOpenChange={setEmailOpen}
          kind="rfq"
          defaultTo={detailRfq.supplier_snapshot?.email || ""}
          defaultClientName={detailRfq.supplier_snapshot?.name || "Supplier"}
          testIdPrefix="rfq-email"
          sendFn={async (payload) => {
            const res = await api.emailRfq(detailRfq.id, payload);
            qc.invalidateQueries({ queryKey: ["rfqs"] });
            setDetailRfq({
              ...detailRfq,
              status: detailRfq.status === "draft" ? "sent" : detailRfq.status,
              last_emailed_to: payload.to,
            });
            return res;
          }}
        />
      )}

      {/* Add manual quote dialog */}
      {detailRfq && (
        <AddQuoteDialog
          open={addQuoteOpen}
          onOpenChange={setAddQuoteOpen}
          rfq={detailRfq}
          suppliers={suppliers}
          onSubmit={(payload) => addQuoteMut.mutate(payload)}
          submitting={addQuoteMut.isPending}
        />
      )}

      {/* Manager approval dialog */}
      <Dialog open={approvalOpen} onOpenChange={setApprovalOpen}>
        <DialogContent data-testid="rfq-approval-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="size-5 text-emerald-700" />
              Approve winning quote
            </DialogTitle>
            <DialogDescription className="text-xs">
              Sign off on this winner so the PO can be issued. This action is
              logged and stays on the RFQ.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="overline">Manager name *</Label>
              <Input
                data-testid="rfq-approval-name"
                value={approverName}
                onChange={(e) => setApproverName(e.target.value)}
                placeholder="e.g. Joaquim Martins"
              />
            </div>
            <div>
              <Label className="overline">Notes (optional)</Label>
              <Textarea
                data-testid="rfq-approval-notes"
                rows={2}
                value={approverNotes}
                onChange={(e) => setApproverNotes(e.target.value)}
                placeholder="Any decision context (e.g. delivered faster, better warranty)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setApprovalOpen(false)}
              data-testid="rfq-approval-cancel"
            >
              Cancel
            </Button>
            <Button
              data-testid="rfq-approval-submit"
              disabled={approveMut.isPending || !approverName.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => approveMut.mutate()}
            >
              {approveMut.isPending ? "Signing…" : "Sign & approve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={!!confirmDeleteId}
        onOpenChange={(v) => !v && setConfirmDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this RFQ?</AlertDialogTitle>
            <AlertDialogDescription>
              This can&apos;t be undone. Any linked purchase order stays intact.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rfq-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="rfq-delete-confirm"
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteMut.mutate(confirmDeleteId)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function KPI({ testid, label, icon: Icon, value, tone }) {
  const toneMap = {
    slate: "border-slate-200 bg-white",
    amber: "border-amber-200 bg-amber-50",
    emerald: "border-emerald-200 bg-emerald-50",
    sky: "border-sky-200 bg-sky-50",
  };
  const iconMap = {
    slate: "text-slate-700 bg-slate-100",
    amber: "text-amber-800 bg-amber-100",
    emerald: "text-emerald-700 bg-emerald-100",
    sky: "text-sky-700 bg-sky-100",
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-md border p-4 flex items-center justify-between ${toneMap[tone]}`}
    >
      <div>
        <p className="overline">{label}</p>
        <p className="text-2xl font-bold text-foreground mt-1 font-mono">{value}</p>
      </div>
      <span className={`size-10 grid place-items-center rounded-full ${iconMap[tone]}`}>
        <Icon className="size-5" />
      </span>
    </div>
  );
}

function QuotesCompareTable({ rfq, onSelectWinner, onRemoveQuote }) {
  const quotes = rfq.quotes || [];
  const lines = rfq.lines || [];
  const winnerId = rfq.winner_quote_id;
  return (
    <div className="overflow-x-auto" data-testid="rfq-quotes-compare">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent border-b border-border">
            <TableHead className="overline">Item</TableHead>
            <TableHead className="overline text-right">Qty</TableHead>
            <TableHead className="overline text-right">Target</TableHead>
            {quotes.map((q) => (
              <TableHead
                key={q.id}
                className={`overline text-right ${winnerId === q.id ? "text-emerald-700" : ""}`}
                data-testid={`rfq-quote-col-${q.id}`}
              >
                <div className="flex flex-col items-end gap-0.5">
                  <span className="font-semibold">
                    {(q.supplier_snapshot || {}).name || "Supplier"}
                  </span>
                  <span className="text-[9px] font-normal text-muted-foreground">
                    {q.source === "portal" ? "via portal" : "manual"}
                    {(q.currency || rfq.currency) !== rfq.currency && ` · ${q.currency}`}
                  </span>
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((ln, i) => (
            <TableRow key={i}>
              <TableCell className="text-xs">
                <div className="font-medium">{ln.name}</div>
                {ln.specification && (
                  <div className="text-[10px] text-muted-foreground line-clamp-2 whitespace-pre-wrap">
                    {ln.specification}
                  </div>
                )}
              </TableCell>
              <TableCell className="text-right mono text-xs">{ln.qty}</TableCell>
              <TableCell className="text-right mono text-xs text-muted-foreground">
                {ln.target_price > 0 ? fmtPrice(ln.target_price, rfq.currency) : "—"}
              </TableCell>
              {quotes.map((q) => {
                const entry = (q.lines || []).find((p) => Number(p.line_index) === i);
                const price = entry ? Number(entry.unit_price || 0) : 0;
                return (
                  <TableCell
                    key={q.id}
                    className={`text-right mono text-xs ${winnerId === q.id ? "bg-emerald-50" : ""}`}
                  >
                    {price > 0 ? fmtPrice(price, q.currency || rfq.currency) : "—"}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
          <TableRow className="bg-slate-50 border-t border-border">
            <TableCell colSpan={3} className="text-right font-semibold text-xs">
              Total
            </TableCell>
            {quotes.map((q) => {
              const differentCurrency =
                (q.currency || rfq.currency) !== rfq.currency;
              return (
                <TableCell
                  key={q.id}
                  className={`text-right mono font-bold text-xs ${winnerId === q.id ? "text-emerald-700" : "text-primary"}`}
                  data-testid={`rfq-quote-total-${q.id}`}
                >
                  {fmtPrice(q.subtotal, q.currency || rfq.currency)}
                  {differentCurrency && (
                    <div className="text-[9px] font-normal text-muted-foreground">
                      ≈ {fmtPrice(q.subtotal_converted || q.subtotal, rfq.currency)}
                      {q.fx_rate_used && ` @ ${q.fx_rate_used}`}
                    </div>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
          <TableRow>
            <TableCell colSpan={3} className="text-right text-[11px] text-muted-foreground">
              Lead time / valid until
            </TableCell>
            {quotes.map((q) => (
              <TableCell key={q.id} className="text-right text-[10px] text-muted-foreground">
                {q.lead_time_days > 0 ? `${q.lead_time_days}d` : "—"}
                {q.validity && ` · ${q.validity}`}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell colSpan={3} className="text-right">&nbsp;</TableCell>
            {quotes.map((q) => (
              <TableCell key={q.id} className="text-right">
                <div className="flex flex-col items-end gap-1">
                  <Button
                    data-testid={`rfq-quote-winner-${q.id}`}
                    size="sm"
                    className={winnerId === q.id
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white h-7"
                      : "h-7"}
                    variant={winnerId === q.id ? "default" : "outline"}
                    onClick={() => onSelectWinner(q.id)}
                  >
                    <Trophy className="size-3 mr-1" />
                    {winnerId === q.id ? "Winner" : "Pick winner"}
                  </Button>
                  <button
                    data-testid={`rfq-quote-remove-${q.id}`}
                    onClick={() => onRemoveQuote(q.id)}
                    className="text-[10px] text-red-600 hover:underline"
                  >
                    Remove
                  </button>
                </div>
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function AddQuoteDialog({ open, onOpenChange, rfq, suppliers, onSubmit, submitting }) {
  const [supplierId, setSupplierId] = useState("");
  const [leadDays, setLeadDays] = useState(0);
  const [validity, setValidity] = useState("");
  const [notes, setNotes] = useState("");
  const [currency, setCurrency] = useState(rfq.currency || "USD");
  const [prices, setPrices] = useState({});

  useEffect(() => {
    if (open) {
      setSupplierId("");
      setLeadDays(0);
      setValidity("");
      setNotes("");
      setCurrency(rfq.currency || "USD");
      setPrices({});
    }
  }, [open, rfq.currency]);

  const total = (rfq.lines || []).reduce((sum, ln, i) => {
    const p = Number(prices[i]?.unit_price || 0);
    return sum + p * Number(ln.qty || 0);
  }, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="rfq-add-quote-dialog" className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Record supplier quote — {rfq.rfq_number}</DialogTitle>
          <DialogDescription className="text-xs">
            Enter the supplier&apos;s response manually. Empty prices are ignored.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 sm:col-span-5">
            <Label className="overline">Supplier *</Label>
            <Select value={supplierId || undefined} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="rfq-quote-supplier"><SelectValue placeholder="Pick supplier…" /></SelectTrigger>
              <SelectContent className="max-h-60">
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-6 sm:col-span-2">
            <Label className="overline">Currency</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger data-testid="rfq-quote-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["USD", "EUR", "GBP", "AUD", "SGD", "IDR"].map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-6 sm:col-span-2">
            <Label className="overline">Lead (days)</Label>
            <Input
              data-testid="rfq-quote-lead"
              type="number" min="0"
              value={leadDays}
              onChange={(e) => setLeadDays(Number(e.target.value))}
            />
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Label className="overline">Valid until</Label>
            <Input
              data-testid="rfq-quote-validity"
              type="date"
              value={validity}
              onChange={(e) => setValidity(e.target.value)}
            />
          </div>
        </div>

        <div className="border border-border rounded-md overflow-hidden mt-2">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline">Item</TableHead>
                <TableHead className="overline text-right">Qty</TableHead>
                <TableHead className="overline text-right">Unit price ({currency})</TableHead>
                <TableHead className="overline text-right">Lead (days)</TableHead>
                <TableHead className="overline">Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(rfq.lines || []).map((ln, i) => (
                <TableRow key={i}>
                  <TableCell className="text-xs">{ln.name}</TableCell>
                  <TableCell className="text-right mono text-xs">{ln.qty}</TableCell>
                  <TableCell className="text-right">
                    <Input
                      data-testid={`rfq-quote-price-${i}`}
                      type="number" min="0" step="0.01"
                      value={prices[i]?.unit_price ?? ""}
                      onChange={(e) => setPrices((p) => ({
                        ...p, [i]: { ...(p[i] || {}), unit_price: e.target.value },
                      }))}
                      className="h-8 text-right"
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Input
                      data-testid={`rfq-quote-lead-${i}`}
                      type="number" min="0" step="1"
                      value={prices[i]?.lead_time_days ?? ""}
                      onChange={(e) => setPrices((p) => ({
                        ...p, [i]: { ...(p[i] || {}), lead_time_days: e.target.value },
                      }))}
                      className="h-8 text-right"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      data-testid={`rfq-quote-note-${i}`}
                      value={prices[i]?.notes ?? ""}
                      onChange={(e) => setPrices((p) => ({
                        ...p, [i]: { ...(p[i] || {}), notes: e.target.value },
                      }))}
                      className="h-8"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div>
          <Label className="overline">Notes</Label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="rfq-quote-notes" />
        </div>

        <div className="flex justify-end mono text-sm">
          Total: <span className="ml-2 font-bold text-primary" data-testid="rfq-quote-total">
            {fmtPrice(total, currency)}
          </span>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="rfq-quote-cancel">
            Cancel
          </Button>
          <Button
            data-testid="rfq-quote-save"
            disabled={submitting}
            className="bg-primary hover:bg-primary/90"
            onClick={() => {
              if (!supplierId) {
                toast.error("Pick a supplier");
                return;
              }
              const lines = Object.entries(prices)
                .filter(([, v]) => Number(v?.unit_price || 0) > 0)
                .map(([idx, v]) => ({
                  line_index: Number(idx),
                  unit_price: Number(v.unit_price || 0),
                  lead_time_days: Number(v.lead_time_days || 0),
                  notes: (v.notes || "").trim(),
                }));
              if (lines.length === 0) {
                toast.error("Add a price to at least one line");
                return;
              }
              onSubmit({
                supplier_id: supplierId,
                currency,
                lead_time_days: Number(leadDays || 0),
                validity: validity || null,
                notes: notes.trim(),
                lines,
              });
            }}
          >
            {submitting ? "Saving…" : "Save quote"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StaffPortalChat({ rfq, onUpdated }) {
  const [sending, setSending] = useState(false);
  const messages = rfq.portal_messages || [];
  return (
    <ChatPanel
      messages={messages}
      viewerRole="staff"
      isSending={sending}
      testIdPrefix="rfq-chat"
      onSend={async (text) => {
        setSending(true);
        try {
          const updated = await api.postRfqStaffMessage(rfq.id, text);
          onUpdated(updated);
        } finally {
          setSending(false);
        }
      }}
    />
  );
}

